import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { Upload } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useConfirm } from "@/ui/ConfirmDialog.tsx";

import { projectsApi } from "@/api/projects.ts";
import { Alert, Button, Card, Field, Input } from "@/ui/primitives.tsx";
import { useApiError } from "@/ui/useApiError.ts";

/** Outside the component: a component created during render is a new type each time, so React remounts the whole
 *  box on every keystroke and the field being typed in loses focus. */
const BareBox = ({ children }: { children: React.ReactNode }) => <div className="rounded-lg border border-border p-3">{children}</div>;

export function ProjectSecrets({ projectId, bare = false }: { projectId: string; bare?: boolean }) {
  const { t, i18n } = useTranslation();
  const toMsg = useApiError();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["projects", projectId, "secrets"], queryFn: () => projectsApi.secrets(projectId) });
  const [form, setForm] = useState({ key: "", value: "" });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["projects", projectId, "secrets"] });
    void qc.invalidateQueries({ queryKey: ["projects", projectId] });
  };
  const set = useMutation({ mutationFn: () => projectsApi.setSecret(projectId, form.key, form.value), onSuccess: () => { setForm({ key: "", value: "" }); refresh(); } });
  const { confirm } = useConfirm();
  const del = useMutation({ mutationFn: (key: string) => projectsApi.deleteSecret(projectId, key), onSuccess: refresh });
  const askDel = async (key: string) => { if (await confirm({ title: t("confirm.deleteSecretTitle"), message: t("confirm.deleteSecretMsg", { key }), confirmLabel: t("common.delete"), danger: true })) del.mutate(key); };
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<{ entries: { key: string; value: string }[]; skipped: string[]; existing: string[] } | null>(null);
  const parse = useMutation({ mutationFn: (f: File) => projectsApi.parseEnv(projectId, f), onSuccess: setPreview });
  const importAll = useMutation({ mutationFn: () => projectsApi.importSecrets(projectId, preview!.entries), onSuccess: () => { setPreview(null); refresh(); } });
  const fmt = (iso: string) => new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
  const Wrap = bare ? BareBox : Card;
  return (
    <div className="space-y-4">
      <Wrap>
        <form className="grid items-end gap-3 sm:grid-cols-[1fr_2fr_auto]" onSubmit={(e) => { e.preventDefault(); set.mutate(); }}>
          <Field label={t("projects.secrets.key")}>
            <Input dir="ltr" required className="font-mono" pattern="[A-Z_][A-Z0-9_]{0,63}" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value.toUpperCase() })} />
          </Field>
          <Field label={t("projects.secrets.value")}>
            <Input dir="ltr" type="password" autoComplete="off" required className="font-mono" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
          </Field>
          <Button type="submit" loading={set.isPending}>{t("projects.secrets.set")}</Button>
        </form>
        {set.error && <Alert tone="danger" className="mt-3">{toMsg(set.error)}</Alert>}
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-fg-muted">{t("projects.secrets.hint")}</p>
          <input ref={fileRef} type="file" accept=".env,text/plain" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) parse.mutate(f); }} />
          <Button type="button" variant="secondary" size="sm" loading={parse.isPending} onClick={() => fileRef.current?.click()}><Upload className="size-3.5" /> {t("projects.secrets.importEnv")}</Button>
        </div>
        {parse.error && <Alert tone="danger" className="mt-3">{toMsg(parse.error)}</Alert>}
        {preview && (
          <div className="mt-3 space-y-2 rounded-md border border-border bg-surface-2/40 p-3 text-sm">
            <div className="font-medium">{t("projects.secrets.importPreview", { n: preview.entries.length })}</div>
            <ul className="flex flex-wrap gap-1.5" dir="ltr">
              {preview.entries.map((e) => <li key={e.key} className={preview.existing.includes(e.key) ? "rounded border border-warn/50 bg-warn-soft px-1.5 font-mono text-xs" : "rounded border border-border px-1.5 font-mono text-xs"}>{e.key}{preview.existing.includes(e.key) ? ` · ${t("projects.secrets.willReplace")}` : ""}</li>)}
            </ul>
            {preview.skipped.length > 0 && <div className="text-xs text-fg-muted">{t("projects.secrets.importSkipped", { keys: preview.skipped.join(", ") })}</div>}
            {importAll.error && <Alert tone="danger">{toMsg(importAll.error)}</Alert>}
            <div className="flex gap-2">
              <Button size="sm" loading={importAll.isPending} disabled={preview.entries.length === 0} onClick={() => importAll.mutate()}>{t("projects.secrets.importConfirm")}</Button>
              <Button size="sm" variant="ghost" onClick={() => setPreview(null)}>{t("common.cancel")}</Button>
            </div>
          </div>
        )}
        {importAll.isSuccess && importAll.data && <Alert tone="success" className="mt-3">{t("projects.secrets.imported", { added: importAll.data.added.length, replaced: importAll.data.replaced.length })}</Alert>}
      </Wrap>
      <Wrap>
        {q.data?.length === 0 && <p className="text-sm text-fg-muted">{t("projects.secrets.empty")}</p>}
        <ul className="divide-y divide-border text-sm">
          {(q.data ?? []).map((s) => (
            <li key={s.key} className="flex items-center justify-between gap-3 py-2">
              <div>
                <code className="font-mono">{s.key}</code>
                <div className="text-xs text-fg-muted">{t("projects.secrets.updated")}: {fmt(s.updatedAt)}</div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => void askDel(s.key)}>{t("common.delete")}</Button>
            </li>
          ))}
        </ul>
      </Wrap>
    </div>
  );
}
